<script lang="ts" setup>
import {computed, onBeforeUnmount, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {QBtnDropdown, QItem, QItemSection} from 'quasar';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import {useMoods} from '@/ts/composables/pure/use-moods';
import MoodFace from '@/vue/atoms/mood-face.vue';

/**
 * Narrows the journal list to the chosen moods (IPD-3107).
 *
 * Multi-select, unlike the two filters beside it, because asking for "joyful or calm" is a normal
 * thing to want and a single choice would make the user run the query twice. An empty selection
 * means every mood, which is also what the backend reads when the parameter is absent.
 *
 * All ten are always offered. They are a fixed vocabulary, not data, so hiding the ones this
 * period happens to lack would make the menu change shape as the user moves between periods.
 *
 * Shape borrowed from `explore-facet-dropdown`, the other multi-select menu in the app: the open
 * state is bound rather than left to Quasar, which otherwise closes the menu on the first pick and
 * makes choosing a second mood impossible; and the rows are `menuitemcheckbox`, which is the ARIA
 * a multi-select menu needs — `option`/`aria-selected` describes a single-choice listbox.
 */
const selected = defineModel<MoodDTO[]>({default: () => []});

const {t: $t} = useI18n();
const {moods} = useMoods();

const isOpen = ref(false);
let scrollListenerTimer = 0;

const label = computed(() => (selected.value.length === 0
  ? $t('journal_filter_all_moods')
  : $t('journal_filter_moods_selected', selected.value.length, {named: {count: selected.value.length}})));

function toggle(mood: MoodDTO): void {
  selected.value = selected.value.includes(mood)
    ? selected.value.filter(m => m !== mood)
    : [...selected.value, mood];
}

function clear(): void {
  selected.value = [];
  isOpen.value = false;
}

// Quasar's menu positions once and does not follow an ancestor scroll container, so an open
// dropdown would float detached once the page scrolls. Same treatment as the Explore facets.
function onOuterScroll(event: Event): void {
  const {target} = event;
  if (target instanceof Element && target.closest('.q-menu')) {
    return;
  }
  isOpen.value = false;
}

function stopScrollWatch(): void {
  window.clearTimeout(scrollListenerTimer);
  window.removeEventListener('scroll', onOuterScroll, {capture: true});
}

watch(isOpen, (open) => {
  if (open) {
    // Deferred: opening scroll-into-views the menu, which would otherwise trip the handler at once.
    window.clearTimeout(scrollListenerTimer);
    scrollListenerTimer = window.setTimeout(() => {
      window.addEventListener('scroll', onOuterScroll, {capture: true, passive: true});
    }, 200);
  } else {
    stopScrollWatch();
  }
});

onBeforeUnmount(stopScrollWatch);
</script>

<template>
  <q-btn-dropdown
    v-model="isOpen"
    class="mood-filter"
    flat
    dense
    no-caps
  >
    <template #label>
      <span class="label">{{ label }}</span>
    </template>

    <!--
      Quasar 2.28 stopped putting role="menu" on the dropdown content, so the list carries it:
      `menuitemcheckbox` rows are only valid inside a menu, and moods are chosen several at a time.
    -->
    <q-list role="menu">
      <q-item
        clickable
        role="menuitemcheckbox"
        :aria-checked="selected.length === 0"
        :class="{'selected-option': selected.length === 0}"
        @click="clear"
      >
        <q-item-section>
          {{ $t('journal_filter_all_moods') }}
        </q-item-section>
      </q-item>

      <q-item
        v-for="mood in moods"
        :key="mood.key"
        clickable
        role="menuitemcheckbox"
        :aria-checked="selected.includes(mood.key)"
        :class="{'selected-option': selected.includes(mood.key)}"
        @click="toggle(mood.key)"
      >
        <q-item-section avatar>
          <mood-face :mood="mood.key" :size="24" tinted/>
        </q-item-section>
        <q-item-section>
          {{ $t(`mood_${mood.key}`) }}
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>
.mood-filter {
  @include focus-visible;
  padding: var(--Spacing-spacing-1) var(--Spacing-spacing-3);
  border-radius: var(--button-border-radius);
}

.label {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-100);
}

.selected-option {
  background-color: var(--Secondary-secondary-color-1);
  color: var(--Neutral-UI-neutral-ui-100);
}
</style>
